app.controller("TranscriptionModalController", [
    "$scope", "$mdDialog", "$interval", "Session", "TranscriptionAPI", "ChunkedRecordingService", "practiceId", "patientId", "activeTranscription", "recentTranscriptionId", "recentLocalText", "TranscriptionLiveUpdateService",
    function ($scope, $mdDialog, $interval, Session, TranscriptionAPI, ChunkedRecordingService, practiceId, patientId, activeTranscription, recentTranscriptionId, recentLocalText, TranscriptionLiveUpdateService) {
        const ctrl = this;
        ctrl.practiceId = practiceId;
        ctrl.patientId = patientId;

        const userId = (Session && Session.user && Session.user.id != null) ? parseInt(Session.user.id, 10) : null;

        // Transcriptions are private to the clinician who recorded them. The API only returns the
        // user's own, so this is a second line of defence.
        function onlyMine(transcriptions) {
            return (transcriptions || []).filter(t => userId !== null && parseInt(t.created_by_user_id, 10) === userId);
        }

        $scope.isLoading = false;
        $scope.transcriptions = [];
        $scope.selectedTranscription = null;
        $scope.showTimeoutError = false;

        // --- Logic to handle Live Polling ---
        //
        // The completer Lambda publishes a push event when it writes the final text; that normally
        // ends the wait, by running the same fetch the poll would have. The poll is the fallback for
        // a missed event, and asks every fifteen seconds rather than every three.
        let liveTranscriptionRef = (activeTranscription && activeTranscription.isLive) ? activeTranscription : null;
        let pollingInterval = null;
        let pollAttempts = 0;
        let pollFailuresInARow = 0;
        let followedTranscriptionId = null;
        let fetchFollowedTranscription = null;

        const POLL_INTERVAL_MS = 15000;
        const MAX_POLL_ATTEMPTS = 8; // 8 x 15 s = two minutes, as before
        // Failed requests in a row before the poll gives up. Attempts above stop counting while the mic
        // is on, so without this a failing endpoint is asked every 15 s for as long as recording lasts.
        const MAX_POLL_FAILURES_IN_A_ROW = 3;

        TranscriptionLiveUpdateService.subscribe($scope, {
            transcriptionId: function () {
                return followedTranscriptionId;
            },
            onFinished: function () {
                if (fetchFollowedTranscription) {
                    fetchFollowedTranscription();
                }
            },
        });

        if (liveTranscriptionRef) {
            $scope.selectedTranscription = liveTranscriptionRef;

            if (recentLocalText) {
                liveTranscriptionRef.raw_text = recentLocalText;
            }

            $scope.$watch(
                function () {
                    return ChunkedRecordingService.transcriptionId || (liveTranscriptionRef && liveTranscriptionRef.transcription_id);
                },
                function (newId, oldId) {
                    if (newId !== oldId) {
                        stopPolling();
                    }

                    // If ID becomes null/cleared, we stop polling and don't try to restart
                    if (!newId) return;

                    if (liveTranscriptionRef) {
                        liveTranscriptionRef.transcription_id = newId;
                    }

                    startPolling(newId);
                }
            );
        }

        function startPolling(transcriptionId) {
            if (!transcriptionId || pollingInterval) return;
            $scope.showTimeoutError = false;
            pollAttempts = 0; // Reset attempts for a new poll
            pollFailuresInARow = 0;

            const fetchTranscription = () => {
                // Determine if the mic is currently hot for this specific transcription
                const isMicHotForThis = (ChunkedRecordingService.isRecording &&
                    (ChunkedRecordingService.transcriptionId === transcriptionId || ChunkedRecordingService.transcriptionId === null));

                // Only count towards the timeout if we are waiting for the backend to finish. 
                // If we are actively recording, reset the counter so it never times out.
                if (isMicHotForThis) {
                    pollAttempts = 0;
                } else {
                    pollAttempts++;
                }

                if (pollAttempts > MAX_POLL_ATTEMPTS) {
                    console.warn("Modal polling timed out for transcription:", transcriptionId);
                    $scope.showTimeoutError = true;
                    stopPolling();
                    return;
                }

                TranscriptionAPI.getTranscription(ctrl.practiceId, transcriptionId)
                    .then(resp => {
                        pollFailuresInARow = 0;
                        const data = resp.data;
                        if (data) {
                            // Force status to RECORDING locally if the mic is on
                            if (isMicHotForThis) {
                                data.transcription_status = 'RECORDING';
                            }

                            // Only allow it to be "finished" if the mic is actually off
                            const isFinished = !isMicHotForThis && (data.transcription_status === 'COMPLETED' || data.transcription_status === 'FAILED');

                            // Update live reference if applicable
                            if (liveTranscriptionRef && liveTranscriptionRef.transcription_id === transcriptionId) {
                                liveTranscriptionRef.raw_text = data.raw_text;
                                liveTranscriptionRef.transcription_status = data.transcription_status;

                                if (isFinished) liveTranscriptionRef.isLive = false;
                            }

                            // Update selected item in view
                            if ($scope.selectedTranscription && $scope.selectedTranscription.transcription_id === transcriptionId) {
                                $scope.selectedTranscription.raw_text = data.raw_text;
                                $scope.selectedTranscription.transcription_status = data.transcription_status;

                                if (isFinished) $scope.selectedTranscription.isLive = false;
                            }

                            if (isFinished) stopPolling();
                        }
                    })
                    .catch(err => {
                        console.error("Polling failed", err && err.status);
                        pollFailuresInARow++;
                        if (pollFailuresInARow >= MAX_POLL_FAILURES_IN_A_ROW && followedTranscriptionId === transcriptionId) {
                            $scope.showTimeoutError = true;
                            stopPolling();
                        }
                    });
            };

            followedTranscriptionId = transcriptionId;
            fetchFollowedTranscription = fetchTranscription;

            fetchTranscription();
            pollingInterval = $interval(fetchTranscription, POLL_INTERVAL_MS);
        }

        function stopPolling() {
            followedTranscriptionId = null;
            fetchFollowedTranscription = null;
            if (pollingInterval) {
                $interval.cancel(pollingInterval);
                pollingInterval = null;
            }
        }

        $scope.$on('$destroy', function () {
            stopPolling();
        });

        // --- UI Helpers ---
        $scope.isActivelyRecording = function () {
            if (!$scope.selectedTranscription) return false;

            // Clear timeout error if they suddenly start recording again
            if (ChunkedRecordingService.isRecording) {
                $scope.showTimeoutError = false;
            }

            const isMicOn = ChunkedRecordingService.isRecording;
            const currentId = $scope.selectedTranscription.transcription_id || $scope.selectedTranscription.transcriptionId;
            const serviceId = ChunkedRecordingService.transcriptionId;

            const isFinalizing = $scope.selectedTranscription.transcription_status === 'FINALISING_RAW_TEXT' ||
                $scope.selectedTranscription.transcription_status === 'COMPLETING_TRANSCRIPTION';

            return isMicOn && !isFinalizing && (serviceId === currentId || serviceId === null);
        };

        // --- Data Loading ---
        function loadTranscriptions() {
            // SCENARIO 1: Modal opened while live recording
            if ($scope.selectedTranscription && $scope.selectedTranscription.isLive) {
                TranscriptionAPI.getTranscriptionsByPatientId(ctrl.practiceId, ctrl.patientId)
                    .then(resp => {
                        let data = onlyMine(resp.data);

                        // Sort youngest to oldest
                        data.sort((a, b) => new Date(b.date_created) - new Date(a.date_created));
                        $scope.transcriptions = data;
                    });
                return;
            }

            // SCENARIO 2: Modal opened normally
            $scope.isLoading = true;
            TranscriptionAPI.getTranscriptionsByPatientId(ctrl.practiceId, ctrl.patientId)
                .then(function (response) {
                    let data = onlyMine(response.data);

                    // Sort youngest to oldest
                    data.sort((a, b) => new Date(b.date_created) - new Date(a.date_created));

                    if (!activeTranscription && recentTranscriptionId && recentLocalText) {
                        const exists = data.find(t => t.transcription_id === recentTranscriptionId || t.transcriptionId === recentTranscriptionId);
                        if (!exists) {
                            data.unshift({
                                transcription_id: recentTranscriptionId,
                                raw_text: recentLocalText,
                                transcription_status: 'RECORDING', // Fallback status locally
                                date_created: new Date().toISOString()
                            });
                        }
                    }

                    $scope.transcriptions = data;

                    if (!$scope.selectedTranscription && $scope.transcriptions.length > 0) {
                        $scope.viewDetails($scope.transcriptions[0]);
                    }
                })
                .finally(() => { $scope.isLoading = false; });
        }

        loadTranscriptions();

        // --- View Actions ---
        $scope.viewDetails = function (item) {
            // Halt any background polling unless it's the live recording
            if (!item.isLive) stopPolling();

            // Fetch if missing text OR if it's currently processing/finalising
            if (!item.raw_text || item.transcription_status === 'FINALISING_RAW_TEXT' || item.transcription_status === 'COMPLETING_TRANSCRIPTION') {
                $scope.isLoading = true;
                TranscriptionAPI.getTranscription(ctrl.practiceId, item.transcription_id)
                    .then(function (resp) {
                        $scope.selectedTranscription = resp.data;

                        // Begin polling if it's still finalizing
                        if (resp.data.transcription_status === 'FINALISING_RAW_TEXT' || resp.data.transcription_status === 'COMPLETING_TRANSCRIPTION') {
                            startPolling(resp.data.transcription_id);
                        }
                    })
                    .finally(function () {
                        $scope.isLoading = false;
                    });
            } else {
                $scope.selectedTranscription = item;
            }
        };

        $scope.goBack = function () {
            if ($scope.selectedTranscription && !$scope.selectedTranscription.isLive) {
                stopPolling();
            }
            $scope.selectedTranscription = null;
            $scope.showTimeoutError = false; // Reset error when returning to list

            // Resume live polling if an active session exists
            if (liveTranscriptionRef && liveTranscriptionRef.transcription_id) {
                startPolling(liveTranscriptionRef.transcription_id);
            }
        };

        $scope.close = function () {
            stopPolling();
            $mdDialog.hide();
        };

        $scope.formatDuration = function (seconds) {
            if (!seconds) return "00:00";
            var min = Math.floor(seconds / 60);
            var sec = Math.floor(seconds % 60);
            return (min < 10 ? "0" + min : min) + ":" + (sec < 10 ? "0" + sec : sec);
        };
    }
]);